import { useEffect, useId, useState } from "react";
import { Sidebar } from "@/components/layout/Sidebar";
import { AppTopbar } from "@/components/layout/AppTopbar";
import { getAddOnIdentityKey } from "@/lib/addOnIdentity";
import { useAuth } from "@/lib/auth";
import { useAppStore } from "@/lib/store";
import { DEFAULT_ADD_ONS, getAddOns, getAddOnPurchases } from "@/lib/api";

function CartBadgeIcon() {
  const suffix = useId().replace(/:/g, "");
  const filterId = `cart-badge-filter-${suffix}`;
  const paint0Id = `cart-badge-paint0-${suffix}`;
  const paint1Id = `cart-badge-paint1-${suffix}`;

  return (
    <svg width="88" height="88" viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="87" height="87" rx="43.5" fill={`url(#${paint0Id})`} />
      <rect x="0.5" y="0.5" width="87" height="87" rx="43.5" stroke={`url(#${paint1Id})`} />
      <g filter={`url(#${filterId})`}>
        <rect x="16" y="16" width="56" height="56" rx="28" fill="white" />
        <rect x="16.5" y="16.5" width="55" height="55" rx="27.5" stroke="#E2E4E9" />
        <path
          d="M34.5 33.5H37.236L37.728 36H53.5C53.7652 36 54.0196 36.1054 54.2071 36.2929C54.3946 36.4804 54.5 36.7348 54.5 37V49C54.5 49.2652 54.3946 49.5196 54.2071 49.7071C54.0196 49.8946 53.7652 50 53.5 50H38.5C38.2348 50 37.9804 49.8946 37.7929 49.7071C37.6054 49.5196 37.5 49.2652 37.5 49V38.236L36.978 35.5H34.5V33.5ZM39.5 48H52.5V38H38.164L39.5 44.776V48ZM40.5 53.5C39.9696 53.5 39.4609 53.2893 39.0858 52.9142C38.7107 52.5391 38.5 52.0304 38.5 51.5C38.5 50.9696 38.7107 50.4609 39.0858 50.0858C39.4609 49.7107 39.9696 49.5 40.5 49.5C41.0304 49.5 41.5391 49.7107 41.9142 50.0858C42.2893 50.4609 42.5 50.9696 42.5 51.5C42.5 52.0304 42.2893 52.5391 41.9142 52.9142C41.5391 53.2893 41.0304 53.5 40.5 53.5ZM50.5 53.5C49.9696 53.5 49.4609 53.2893 49.0858 52.9142C48.7107 52.5391 48.5 52.0304 48.5 51.5C48.5 50.9696 48.7107 50.4609 49.0858 50.0858C49.4609 49.7107 49.9696 49.5 50.5 49.5C51.0304 49.5 51.5391 49.7107 51.9142 50.0858C52.2893 50.4609 52.5 50.9696 52.5 51.5C52.5 52.0304 52.2893 52.5391 51.9142 52.9142C51.5391 53.2893 51.0304 53.5 50.5 53.5Z"
          fill="#525866"
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="12"
          y="14"
          width="64"
          height="64"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="2" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.105882 0 0 0 0 0.109804 0 0 0 0 0.113725 0 0 0 0.04 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <linearGradient id={paint0Id} x1="44" y1="0" x2="44" y2="88" gradientUnits="userSpaceOnUse">
          <stop stopColor="#E4E5E7" stopOpacity="0.48" />
          <stop offset="1" stopColor="#F7F8F8" stopOpacity="0" />
          <stop offset="1" stopColor="#E4E5E7" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={paint1Id} x1="44" y1="0" x2="44" y2="88" gradientUnits="userSpaceOnUse">
          <stop stopColor="#E4E5E7" />
          <stop offset="0.765625" stopColor="#E4E5E7" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

interface AddOnRow {
  id: string;
  name: string;
  description: string | null;
  price: number;
  price_label: string | null;
  image_url: string | null;
  booking_url: string | null;
  is_active: boolean | null;
  sort_order: number | null;
  created_at: string | null;
}

interface PurchasedAddOn {
  id: string;
  price_paid: number;
  created_at: string;
  add_on_id: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    price_label: string | null;
    image_url: string | null;
    booking_url: string | null;
  };
}

export function AddOnsPage() {
  const { user } = useAuth();
  const { dashboardSearchInput, setDashboardSearchInput, addToCart, cartItems, setCartOpen } = useAppStore();
  const [addOns, setAddOns] = useState<AddOnRow[]>(DEFAULT_ADD_ONS);
  const [loading, setLoading] = useState(false);
  const [purchasedAddOns, setPurchasedAddOns] = useState<PurchasedAddOn[]>([]);

  const displayName =
    user?.user_metadata?.display_name ?? user?.email?.split("@")[0] ?? "User";

  // Silent background sync – defaults render immediately
  useEffect(() => {
    let cancelled = false;
    getAddOns().then(({ data }) => {
      if (!cancelled && data) setAddOns(data);
    });
    if (user?.id) {
      getAddOnPurchases(user.id).then(({ data }) => {
        if (!cancelled && data) setPurchasedAddOns(data as unknown as PurchasedAddOn[]);
      });
    }
    return () => { cancelled = true; };
  }, [user?.id]);

  const addOnKeysInCart = new Set(
    cartItems
      .filter((i) => i.type === "add_on")
      .map((item) => getAddOnIdentityKey(item)),
  );

  const handleAddToCart = (addon: AddOnRow) => {
    addToCart({
      type: "add_on",
      add_on_id: addon.id,
      name: addon.name,
      price: addon.price,
      image_url: addon.image_url ?? undefined,
    });
    setCartOpen(true);
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b] min-[992px]:flex-row">
      <Sidebar />

      <div className="min-w-0 flex-1">
        <AppTopbar
          title={displayName}
          subtitle="Welcome back to BollyRhythm\uD83D\uDC4B\uD83C\uDFFB"
          searchValue={dashboardSearchInput}
          onSearchChange={setDashboardSearchInput}
        />

        {/* shell_inner-wrapper */}
        <div className="h-[calc(100vh-4.5rem)] overflow-auto max-[767px]:h-[calc(100vh-4rem)]">
          {/* padding-global */}
          <div className="px-[clamp(1rem,3vw,3rem)]">
            {/* padding-large */}
            <div className="p-12 max-[991px]:p-10 max-[767px]:p-6">
              {/* add_ons_content-wrapper */}
              <div className="pt-4 pb-20">
                {/* add_ons_inner-wrapper */}
                <div className="mx-auto flex max-w-[728px] flex-col gap-16 max-[767px]:gap-12">
                  {/* add_ons_header-wrapper */}
                  <div className="flex flex-col items-center justify-start gap-[6px]">
                    <CartBadgeIcon />
                    <h3 className="m-0 font-[family-name:var(--font-display)] text-[clamp(1.5rem,1.2143rem+1.4286vw,2.5rem)] font-medium leading-[1.2] text-[#1f0a18]">
                      Choose your add ons
                    </h3>
                    <p className="m-0 text-center font-medium text-[#7c7c7c]">
                      Just a couple extras to make your performance even smoother
                    </p>
                  </div>

                  {/* Purchased add-ons */}
                  {purchasedAddOns.length > 0 && (
                    <div className="flex flex-col gap-4">
                      <h4 className="m-0 text-[0.78rem] font-[800] uppercase tracking-[0.08em] text-[#9b8795]">
                        Your Purchased Add-Ons
                      </h4>
                      <div className="flex flex-col gap-3">
                        {purchasedAddOns.map((purchase) => (
                          <div
                            key={purchase.id}
                            className="flex items-center gap-4 rounded-[1rem] border border-[#d6f5d6] bg-[#f4fdf4] p-4 max-[767px]:flex-col max-[767px]:items-start"
                          >
                            {purchase.add_on_id.image_url && (
                              <img
                                src={purchase.add_on_id.image_url}
                                alt={purchase.add_on_id.name}
                                className="h-16 w-24 rounded-[0.6rem] object-cover max-[767px]:h-32 max-[767px]:w-full"
                                loading="lazy"
                              />
                            )}
                            <div className="flex-1">
                              <div className="text-base font-bold text-[#1f0a18]">
                                {purchase.add_on_id.name}
                              </div>
                              <div className="mt-1 text-[0.84rem] text-[#525866]">
                                Purchased on{" "}
                                {new Date(purchase.created_at).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                                {" · "}${purchase.price_paid}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {loading ? (
                    <div className="grid grid-cols-2 gap-6 max-[767px]:grid-cols-1">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="h-[22rem] animate-pulse rounded-[1rem] border border-[#e2e4e9] bg-white" />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-6 max-[767px]:grid-cols-1">
                      {addOns.map((addon) => {
                        const inCart = addOnKeysInCart.has(getAddOnIdentityKey(addon));
                        return (
                          <article
                            key={addon.id}
                            className="flex flex-col gap-[1.55rem] rounded-[1rem] border border-[#e2e4e9] p-6 pb-4 max-[767px]:p-[0.85rem]"
                          >
                            {addon.image_url && (
                              <div className="relative aspect-[3/2]">
                                <img
                                  src={addon.image_url}
                                  alt={addon.name}
                                  className="absolute inset-0 h-full w-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                            )}

                            <div className="mt-auto flex flex-col gap-5">
                              <h3 className="m-0 font-[family-name:var(--font-display)] text-[clamp(1.25rem,1.0357rem+1.0714vw,1.75rem)] font-medium leading-[1.2] text-[#212121]">
                                {addon.name}
                              </h3>

                              <p className="m-0 font-[family-name:Inter,sans-serif] text-[0.875rem] font-normal leading-[1.5] text-[#525866]">
                                {addon.description}
                              </p>

                              <div className="mt-auto flex items-center justify-start gap-[6px]">
                                <span className="font-[family-name:var(--font-display)] text-[1.6rem] font-semibold min-[480px]:text-[2rem]">
                                  ${addon.price}
                                </span>
                                {addon.price_label && (
                                  <span className="font-[family-name:Inter,sans-serif] text-[0.875rem] text-black/45">
                                    {addon.price_label}
                                  </span>
                                )}
                              </div>

                              <button
                                type="button"
                                className={`mt-auto inline-flex w-full items-center justify-center gap-2 rounded-[0.5rem] px-8 py-4 text-center font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none transition-transform duration-200 ease-[cubic-bezier(.39,.575,.565,1)] hover:-translate-y-[5%] active:scale-95 ${
                                  inCart
                                    ? "border border-[#762a5c] bg-white text-[#762a5c]"
                                    : "bg-[#762a5c] text-white"
                                }`}
                                style={{ minHeight: "51px" }}
                                onClick={() => handleAddToCart(addon)}
                              >
                                {inCart ? "Added to Cart" : "Add to Cart"}
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
